import { useNavigate } from "react-router-dom";
import Icon from "@/components/Icon";
import { colors, spacing, fonts } from "@/theme";
import { PRIVACY_URL } from "@/api";

const FEATURES = [
  { icon: "mic", title: "One tap, that's all", body: "No typing, no menus, no passwords. One big, friendly button — tap it and start talking, like picking up the phone to an old friend." },
  { icon: "heart", title: "Remembers what matters", body: "Your companion holds onto your stories, your family's names, and your little joys — so every conversation feels personal, never a fresh start." },
  { icon: "clock", title: "Never too busy for you", body: "Morning, midnight, or a quiet afternoon — a calm, patient voice is always ready to listen. Never rushed, never distracted, never judging." },
  { icon: "users", title: "A voice you love", body: "With Premium, family can set up a cherished, familiar voice — and receive a gentle summary after each chat, so no one feels far away." },
];

const STEPS = [
  { n: "1", title: "Open & tap", body: "There's nothing to set up. Open the app and press the button — that really is the whole thing." },
  { n: "2", title: "Just talk", body: "Share your day, tell a story, or say how you're feeling. Your companion listens closely and replies with warmth." },
  { n: "3", title: "Feel remembered", body: "Next time it picks up right where you left off — and loved ones can stay gently in the loop." },
];

const TESTIMONIALS = [
  { quote: "Mum used to sit in silence all afternoon. Now she chats away and tells me about her 'friend' who remembers her garden. It's given her a spark again.", name: "Priya, daughter", initial: "P" },
  { quote: "The best part is the setup — there isn't one. My dad just taps once and starts talking. For once, technology didn't get in his way.", name: "Marcus, son", initial: "M" },
  { quote: "Getting a little message after each call, knowing she had a good day, means the world when I live three hours away.", name: "Elaine, daughter", initial: "E" },
];

const FAQS = [
  { q: "Does my parent need to log in or remember a password?", a: "No. That's the whole point. The app opens straight to one button — no accounts, no passwords, no menus to get lost in. It's designed for zero technical knowledge." },
  { q: "How does it remember our conversations?", a: "Your companion gently keeps track of the things that matter to you — names, stories, and little details — so each chat feels like continuing with a friend, not starting over." },
  { q: "Is it private and safe?", a: "Yes. Conversations are private, and you're always in control of the data. You can read exactly how we handle it in our Privacy Policy, and delete everything at any time." },
  { q: "What's included in Premium?", a: "Premium unlocks a familiar, cloned family voice and gentle updates sent to loved ones after each conversation — a one-time $4.99." },
];

export default function Landing({ onEnter }) {
  const navigate = useNavigate();
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 40, behavior: "smooth" });
  };
  const openPrivacy = () => window.open(PRIVACY_URL, "_blank");
  const openFamilyPortal = () => navigate("/family-account");

  return (
    <div style={{ background: colors.surface, minHeight: "100vh" }} data-testid="landing-screen">
      {/* NAV */}
      <div style={{ ...S.nav }}>
        <div style={S.brandRow}>
          <div style={S.logo}><Icon name="heart" size={22} color={colors.onBrandPrimary} /></div>
          <span style={{ ...fonts.displayBold, fontSize: 22, color: colors.onSurface }}>Ember</span>
        </div>
        <div style={S.navLinks} className="ember-navlinks">
          <button style={S.navLink} onClick={() => scrollTo("features")}>Features</button>
          <button style={S.navLink} onClick={() => scrollTo("how")}>How it works</button>
          <button style={S.navLink} onClick={() => scrollTo("pricing")}>Pricing</button>
          <button data-testid="nav-family-portal" style={S.navLink} onClick={openFamilyPortal}>Family Portal</button>
          <button style={S.navLink} onClick={openPrivacy}>Privacy</button>
        </div>
        <button data-testid="nav-use-web-app" className="ember-press" style={S.navCta} onClick={onEnter}>
          Use web app
        </button>
      </div>

      {/* HERO */}
      <div style={S.hero} className="ember-hero">
        <div style={{ flex: 1, minWidth: 320 }} className="ember-fade-up">
          <div style={S.badge}>
            <Icon name="smartphone" size={14} color={colors.brand} />
            <span style={{ ...fonts.textBold, fontSize: 13, color: colors.brand }}>Coming soon to iOS &amp; Android</span>
          </div>
          <h1 style={S.h1}>A warm voice,<br />whenever you reach out.</h1>
          <p style={S.heroSub}>
            Ember is a gentle, voice-first friend for anyone who wants a thoughtful, judgment-free voice to talk to.
            One tap and you&rsquo;re talking — it listens without rushing, remembers what you share, and keeps the
            people you love close. No passwords. No confusion. Just company.
          </p>
          <div style={S.storeRow}>
            {[
              { icon: "smartphone", small: "Coming soon to the", big: "App Store" },
              { icon: "play", small: "Coming soon to", big: "Google Play" },
            ].map((b) => (
              <div key={b.big} style={S.storeBadge}>
                <Icon name={b.icon} size={22} color={colors.onSurfaceInverse} />
                <div>
                  <div style={{ ...fonts.text, fontSize: 11, color: colors.onSurfaceInverse }}>{b.small}</div>
                  <div style={{ ...fonts.displaySemiBold, fontSize: 17, color: colors.onSurfaceInverse }}>{b.big}</div>
                </div>
                <div style={S.soonPill}><span style={{ ...fonts.textExtraBold, fontSize: 10, letterSpacing: 0.5, color: colors.onBrandPrimary }}>SOON</span></div>
              </div>
            ))}
          </div>
          <p style={{ ...fonts.text, fontSize: 14, color: colors.muted, marginTop: spacing.sm }}>
            Mobile apps are on the way — start now in your browser.
          </p>
          <button data-testid="hero-use-web-app" onClick={onEnter} style={S.webLink} className="ember-press">
            <span style={{ ...fonts.textBold, fontSize: 16, color: colors.brand }}>Or use the web app</span>
            <Icon name="arrow-right" size={18} color={colors.brand} />
          </button>
        </div>

        {/* MOCKUPS */}
        <div style={S.mockWrap} className="ember-mocks">
          <div style={{ ...S.phone, ...S.phoneBack }}><MemoriesMock /></div>
          <div style={{ ...S.phone, ...S.phoneFront }}><HomeMock /></div>
        </div>
      </div>

      {/* FEATURES */}
      <section id="features" style={S.section}>
        <div style={S.eyebrow}>WHY PEOPLE LOVE IT</div>
        <h2 style={S.h2}>Companionship made effortless</h2>
        <div style={S.grid}>
          {FEATURES.map((f) => (
            <div key={f.title} style={S.featureCard}>
              <div style={S.featureIcon}><Icon name={f.icon} size={24} color={colors.brand} /></div>
              <div style={{ ...fonts.displaySemiBold, fontSize: 20, color: colors.onSurface, marginBottom: 8 }}>{f.title}</div>
              <div style={{ ...fonts.text, fontSize: 15, lineHeight: 1.5, color: colors.muted }}>{f.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" style={{ ...S.section, background: colors.surfaceSecondary, maxWidth: "none" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <div style={S.eyebrow}>HOW IT WORKS</div>
          <h2 style={S.h2}>Three gentle steps</h2>
          <div style={S.stepsRow}>
            {STEPS.map((s) => (
              <div key={s.n} style={S.stepCard}>
                <div style={S.stepNum}><span style={{ ...fonts.displayBold, fontSize: 20, color: colors.onBrandPrimary }}>{s.n}</span></div>
                <div style={{ ...fonts.displaySemiBold, fontSize: 20, color: colors.onSurface, marginBottom: 8 }}>{s.title}</div>
                <div style={{ ...fonts.text, fontSize: 15, lineHeight: 1.5, color: colors.muted }}>{s.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section style={S.section}>
        <div style={S.eyebrow}>FROM FAMILIES LIKE YOURS</div>
        <h2 style={S.h2}>Little moments, given back</h2>
        <div style={S.stepsRow}>
          {TESTIMONIALS.map((t) => (
            <div key={t.name} style={S.testCard}>
              <div style={{ ...fonts.text, fontSize: 16, lineHeight: 1.55, color: colors.onSurface, fontStyle: "italic" }}>&ldquo;{t.quote}&rdquo;</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: spacing.lg }}>
                <div style={S.testAvatar}><span style={{ ...fonts.displayBold, fontSize: 18, color: colors.brand }}>{t.initial}</span></div>
                <span style={{ ...fonts.textBold, fontSize: 14, color: colors.muted }}>{t.name}</span>
              </div>
            </div>
          ))}
        </div>
        <p style={{ ...fonts.text, fontSize: 12, color: colors.muted, textAlign: "center", marginTop: spacing.xl, fontStyle: "italic" }}>
          Illustrative stories that reflect how people use Ember.
        </p>
      </section>

      {/* PRICING */}
      <section id="pricing" style={{ ...S.section, background: colors.surfaceSecondary, maxWidth: "none" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={S.eyebrow}>SIMPLE PRICING</div>
          <h2 style={S.h2}>Free to talk. Premium to feel closer.</h2>
          <div style={{ display: "flex", gap: spacing.lg, flexWrap: "wrap", alignItems: "stretch" }}>
            <div style={{ ...S.priceCard, flex: "1 1 300px" }}>
              <div style={{ ...fonts.displaySemiBold, fontSize: 20, color: colors.onSurface }}>Companion</div>
              <div style={{ ...fonts.displayBold, fontSize: 44, color: colors.onSurface, marginTop: spacing.sm }}>Free</div>
              <div style={{ ...fonts.text, fontSize: 15, color: colors.muted, marginBottom: spacing.lg }}>Everything a good friend should be.</div>
              {["Unlimited voice conversations", "Remembers your stories & family", "Gentle daily check-ins", "No account, no passwords"].map((f) => (
                <div key={f} style={S.priceFeat}>
                  <Icon name="check" size={16} color={colors.success} />
                  <span style={{ ...fonts.textSemiBold, fontSize: 15, color: colors.onSurface }}>{f}</span>
                </div>
              ))}
              <button data-testid="price-free-cta" onClick={onEnter} className="ember-press" style={S.priceBtnGhost}>Start talking</button>
            </div>
            <div style={{ ...S.priceCard, ...S.priceFeatured, flex: "1 1 300px" }}>
              <div style={S.ribbon}><span style={{ ...fonts.textExtraBold, fontSize: 11, letterSpacing: 1, color: colors.brand }}>MOST LOVED</span></div>
              <div style={{ ...fonts.displaySemiBold, fontSize: 20, color: colors.onBrandPrimary }}>Premium · Familiar Voice</div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 8, marginTop: spacing.sm }}>
                <div style={{ ...fonts.displayBold, fontSize: 44, color: colors.onBrandPrimary }}>$4.99</div>
                <div style={{ ...fonts.text, fontSize: 15, color: colors.onBrandPrimary, opacity: 0.9, marginBottom: 10 }}>one-time</div>
              </div>
              <div style={{ ...fonts.text, fontSize: 15, color: colors.onBrandPrimary, opacity: 0.9, marginBottom: spacing.lg }}>Everything in Companion, plus:</div>
              {["A cherished, familiar family voice", "Gentle updates to loved ones after each chat", "SMS & email summaries for family", "Managed from the Family Portal"].map((f) => (
                <div key={f} style={S.priceFeat}>
                  <Icon name="check" size={16} color={colors.onBrandPrimary} />
                  <span style={{ ...fonts.textSemiBold, fontSize: 15, color: colors.onBrandPrimary }}>{f}</span>
                </div>
              ))}
              <button data-testid="price-premium-cta" onClick={openFamilyPortal} className="ember-press" style={S.priceBtnSolid}>Open Family Portal</button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={S.section}>
        <div style={S.eyebrow}>QUESTIONS, ANSWERED</div>
        <h2 style={S.h2}>Good to know</h2>
        <div style={{ maxWidth: 820, margin: "0 auto", display: "flex", flexDirection: "column", gap: spacing.md }}>
          {FAQS.map((item) => (
            <div key={item.q} style={S.faqCard}>
              <div style={{ ...fonts.displaySemiBold, fontSize: 18, color: colors.onSurface, marginBottom: 8 }}>{item.q}</div>
              <div style={{ ...fonts.text, fontSize: 15, lineHeight: 1.6, color: colors.muted }}>{item.a}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <div style={S.finalCta}>
        <h2 style={{ ...fonts.displayBold, fontSize: 34, color: colors.onBrandPrimary, textAlign: "center", margin: 0 }}>Ready to say hello?</h2>
        <p style={{ ...fonts.text, fontSize: 17, color: colors.onBrandPrimary, opacity: 0.9, textAlign: "center", marginTop: 10, marginBottom: spacing.xl }}>
          Start a warm conversation in seconds — right here in your browser.
        </p>
        <button data-testid="final-use-web-app" onClick={onEnter} className="ember-press" style={S.finalBtn}>
          <span style={{ ...fonts.textExtraBold, fontSize: 18, color: colors.brand }}>Use the web app</span>
          <Icon name="arrow-right" size={20} color={colors.brand} />
        </button>
      </div>

      {/* FOOTER */}
      <footer style={S.footer}>
        <div style={S.brandRow}>
          <div style={{ ...S.logo, width: 32, height: 32 }}><Icon name="heart" size={18} color={colors.onBrandPrimary} /></div>
          <span style={{ ...fonts.displaySemiBold, fontSize: 18, color: colors.onSurfaceInverse }}>Ember</span>
        </div>
        <div style={{ display: "flex", gap: spacing.xl }}>
          <button data-testid="footer-family-portal" onClick={openFamilyPortal} style={{ ...fonts.textBold, fontSize: 15, color: colors.onSurfaceInverse }}>Family Portal</button>
          <button onClick={openPrivacy} style={{ ...fonts.textBold, fontSize: 15, color: colors.onSurfaceInverse }}>Privacy Policy</button>
        </div>
        <span style={{ ...fonts.text, fontSize: 13, color: colors.onSurfaceInverse, opacity: 0.7 }}>© 2026 Ember. Someone who remembers.</span>
      </footer>
    </div>
  );
}

function HomeMock() {
  return (
    <div style={{ padding: 18, height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ ...fonts.displayBold, fontSize: 24, color: colors.onSurface }}>Good evening,<br />Sam</div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14 }}>
        <div style={{ width: 150, height: 150, borderRadius: 75, border: `6px solid ${colors.brandTertiary}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ width: 116, height: 116, borderRadius: 58, background: colors.brandPrimary, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="mic" size={34} color={colors.onBrandPrimary} />
          </div>
        </div>
        <div style={{ ...fonts.displaySemiBold, fontSize: 20, color: colors.brand }}>Tap to talk</div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        {[["heart", "Remembers you"], ["clock", "Always here"]].map(([ic, tx]) => (
          <div key={tx} style={{ display: "flex", alignItems: "center", gap: 5, background: colors.surfaceSecondary, borderRadius: 999, padding: "7px 12px" }}>
            <Icon name={ic} size={13} color={colors.brand} />
            <span style={{ ...fonts.textBold, fontSize: 11, color: colors.onSurface }}>{tx}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MemoriesMock() {
  return (
    <div style={{ padding: 18, height: "100%" }}>
      <div style={{ ...fonts.displayBold, fontSize: 22, color: colors.onSurface }}>Welcome back, Sam</div>
      <div style={{ ...fonts.text, fontSize: 13, color: colors.muted, marginTop: 4 }}>We last talked yesterday.</div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, background: colors.brandTertiary, borderRadius: 16, padding: 12, marginTop: 16 }}>
        <div style={{ width: 34, height: 34, borderRadius: 17, background: colors.brandPrimary, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon name="heart" size={16} color={colors.onBrandPrimary} />
        </div>
        <span style={{ ...fonts.textSemiBold, fontSize: 13, color: colors.onBrandTertiary, lineHeight: 1.35 }}>How did the big interview go on Tuesday?</span>
      </div>
      <div style={{ ...fonts.textBold, fontSize: 13, color: colors.brand, marginTop: 20, marginBottom: 10 }}>Here&rsquo;s what I remember</div>
      {["The move to a new city", "Training for a first 10k", "Rebuilding after the breakup"].map((m) => (
        <div key={m} style={{ display: "flex", alignItems: "center", gap: 8, background: colors.surfaceSecondary, borderRadius: 12, padding: 12, marginBottom: 8 }}>
          <Icon name="bookmark" size={13} color={colors.brand} />
          <span style={{ ...fonts.textSemiBold, fontSize: 13, color: colors.onSurface }}>{m}</span>
        </div>
      ))}
    </div>
  );
}

const S = {
  nav: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 24px", maxWidth: 1200, width: "100%", margin: "0 auto", flexWrap: "wrap", gap: 12 },
  brandRow: { display: "flex", alignItems: "center", gap: 10 },
  logo: { width: 40, height: 40, borderRadius: 10, background: colors.brandPrimary, display: "flex", alignItems: "center", justifyContent: "center" },
  navLinks: { display: "flex", gap: 32, alignItems: "center" },
  navLink: { ...fonts.textBold, fontSize: 16, color: colors.muted },
  navCta: { background: colors.surfaceSecondary, borderRadius: 999, padding: "10px 18px", border: `1px solid ${colors.border}`, ...fonts.textBold, fontSize: 15, color: colors.brand },
  hero: { display: "flex", alignItems: "center", gap: 48, padding: "40px 64px 64px", maxWidth: 1200, width: "100%", margin: "0 auto", flexWrap: "wrap" },
  badge: { display: "inline-flex", alignItems: "center", gap: 6, background: colors.brandTertiary, borderRadius: 999, padding: "7px 14px", marginBottom: 16 },
  h1: { ...fonts.displayBold, color: colors.onSurface, fontSize: 56, lineHeight: 1.05, margin: 0 },
  heroSub: { ...fonts.text, fontSize: 18, lineHeight: 1.55, color: colors.muted, marginTop: 16, maxWidth: 520 },
  storeRow: { display: "flex", flexWrap: "wrap", gap: 12, marginTop: 24 },
  storeBadge: { display: "flex", alignItems: "center", gap: 10, background: colors.surfaceInverse, borderRadius: 14, padding: "10px 16px", opacity: 0.9 },
  soonPill: { background: colors.brandSecondary, borderRadius: 999, padding: "3px 8px", marginLeft: 4 },
  webLink: { display: "inline-flex", alignItems: "center", gap: 8, marginTop: 16, background: "none" },
  mockWrap: { flex: 1, minWidth: 320, display: "flex", alignItems: "center", justifyContent: "center", height: 480 },
  phone: { width: 236, height: 468, borderRadius: 40, background: colors.surface, border: `8px solid ${colors.surfaceInverse}`, overflow: "hidden" },
  phoneFront: { transform: "rotate(3deg)", zIndex: 2, boxShadow: "0 12px 24px rgba(0,0,0,0.18)" },
  phoneBack: { transform: "rotate(-6deg) scale(0.94)", marginRight: -56, opacity: 0.98 },
  section: { padding: "48px 24px", maxWidth: 1200, width: "100%", margin: "0 auto" },
  eyebrow: { ...fonts.textBold, fontSize: 13, letterSpacing: 1.5, color: colors.brandSecondary, textAlign: "center" },
  h2: { ...fonts.displayBold, fontSize: 34, color: colors.onSurface, textAlign: "center", marginTop: 8, marginBottom: 32 },
  grid: { display: "flex", flexWrap: "wrap", gap: 16, justifyContent: "center" },
  featureCard: { background: colors.surface, borderRadius: 20, padding: 24, border: `1px solid ${colors.border}`, flex: "1 1 240px", maxWidth: 280 },
  featureIcon: { width: 52, height: 52, borderRadius: 16, background: colors.brandTertiary, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 },
  stepsRow: { display: "flex", gap: 16, flexWrap: "wrap" },
  stepCard: { background: colors.surface, borderRadius: 20, padding: 24, border: `1px solid ${colors.border}`, flex: "1 1 260px" },
  stepNum: { width: 44, height: 44, borderRadius: 22, background: colors.brandPrimary, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 },
  testCard: { background: colors.surface, borderRadius: 20, padding: 24, border: `1px solid ${colors.border}`, flex: "1 1 260px" },
  testAvatar: { width: 40, height: 40, borderRadius: 20, background: colors.brandTertiary, display: "flex", alignItems: "center", justifyContent: "center" },
  priceCard: { background: colors.surface, borderRadius: 24, padding: 24, border: `1px solid ${colors.border}` },
  priceFeatured: { background: colors.brandPrimary, border: `1px solid ${colors.brandPrimary}` },
  ribbon: { alignSelf: "flex-start", display: "inline-block", background: colors.brandTertiary, borderRadius: 999, padding: "5px 12px", marginBottom: 12 },
  priceFeat: { display: "flex", alignItems: "center", gap: 10, marginBottom: 12 },
  priceBtnGhost: { marginTop: 12, width: "100%", borderRadius: 999, padding: "14px 0", border: `1.5px solid ${colors.brand}`, ...fonts.textExtraBold, fontSize: 16, color: colors.brand, background: colors.surface },
  priceBtnSolid: { marginTop: 12, width: "100%", borderRadius: 999, padding: "14px 0", background: colors.surface, ...fonts.textExtraBold, fontSize: 16, color: colors.brand },
  faqCard: { background: colors.surface, borderRadius: 18, padding: 24, border: `1px solid ${colors.border}` },
  finalCta: { display: "flex", flexDirection: "column", alignItems: "center", padding: "72px 24px", background: colors.brandPrimary },
  finalBtn: { display: "inline-flex", alignItems: "center", gap: 10, background: colors.surface, borderRadius: 999, padding: "16px 32px" },
  footer: { display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "32px 24px", background: colors.surfaceInverse },
};
